import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Calendar, CheckSquare, Square } from 'lucide-react';
// @ts-ignore - JSX module
import { PhotoThumbnail, usePhotoLightbox, getGuestPhotoUrl, isVideoDoc } from '@/components/ui/PhotoGallery';
import GuestVideoDialog from './GuestVideoDialog';
import { backendToFrontendFieldType } from '@/lib/utils/procedure-field-types';
import { getIntlLocaleTag } from '@/lib/i18n';

/**
 * Formate une date ISO selon la langue active (DD/MM/YYYY en fr, DD/MM/YYYY en en-GB, etc.)
 */
function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleDateString(getIntlLocaleTag());
    } catch {
        return dateString;
    }
}

/**
 * Grille de médias : lightbox pour les photos, lecteur invité pour les vidéos
 */
function PhotoFieldView({ photos, onVideoClick }: { photos: any[]; onVideoClick: (video: any) => void }) {
    const { openLightbox } = usePhotoLightbox(photos);

    return (
        <div className="flex flex-wrap gap-[8px]">
            {photos.map((photo: any, index: number) => (
                <PhotoThumbnail
                    key={photo.uniq_id || index}
                    photo={photo}
                    index={index}
                    showRemoveButton={false}
                    showHorodateBadge={true}
                    onClick={
                        photo.uniq_id
                            ? () => (isVideoDoc(photo) ? onVideoClick(photo) : openLightbox(index))
                            : undefined
                    }
                    width={200}
                    height={200}
                />
            ))}
        </div>
    );
}

/**
 * Affichage read-only d'un champ
 */
function ReadOnlyField({
    field,
    value,
    photos,
    onVideoClick,
}: {
    field: any;
    value: any;
    photos: any[];
    onVideoClick: (video: any) => void;
}) {
    const { t } = useTranslation('public');
    const frontendType = backendToFrontendFieldType(field.type);

    return (
        <div>
            <label className="text-[12px] text-muted-foreground mb-[6px] block">
                {field.label}
            </label>

            {frontendType === 'photo' || frontendType === 'video' ? (
                photos.length > 0 ? (
                    <PhotoFieldView photos={photos} onVideoClick={onVideoClick} />
                ) : (
                    <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                        <p className="text-[14px] text-muted-foreground italic">
                            {t(frontendType === 'video' ? 'report.noVideo' : 'report.noPhoto')}
                        </p>
                    </div>
                )
            ) : frontendType === 'checkbox' && field.options?.length > 0 ? (
                <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border space-y-[8px]">
                    {(field.options || []).map((option: string, optIndex: number) => {
                        const isChecked = Array.isArray(value)
                            ? value.includes(option)
                            : value === option;
                        return (
                            <div key={optIndex} className="flex items-center gap-[8px]">
                                {isChecked ? (
                                    <CheckSquare className="h-4 w-4 text-brand" />
                                ) : (
                                    <Square className="h-4 w-4 text-muted-foreground" />
                                )}
                                <span className={`text-[14px] ${isChecked ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {option}
                                </span>
                            </div>
                        );
                    })}
                </div>
            ) : frontendType === 'date' ? (
                <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                    <p className="text-[14px] text-foreground">
                        {value ? (
                            formatDate(value)
                        ) : (
                            <span className="text-muted-foreground italic">{t('report.notProvided')}</span>
                        )}
                    </p>
                </div>
            ) : frontendType === 'signature' ? (
                <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                    {value ? (
                        <div className="flex items-center gap-[12px]">
                            <img
                                src={value}
                                alt={t('public:signature')}
                                className="max-h-[120px] w-auto rounded-[8px] border border-border bg-card"
                            />
                            <div className="flex items-center gap-[6px] text-brand">
                                <CheckSquare className="h-4 w-4" />
                                <span className="text-[12px] font-medium">{t('report.signed')}</span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-[14px] text-muted-foreground italic">{t('report.notSigned')}</p>
                    )}
                </div>
            ) : (
                <div className="bg-surface rounded-[12px] px-[14px] py-[12px] border border-border">
                    <p className="text-[14px] text-foreground">
                        {Array.isArray(value)
                            ? value.join(', ')
                            : value || (
                                  <span className="text-muted-foreground italic">{t('report.notProvided')}</span>
                              )}
                    </p>
                </div>
            )}
        </div>
    );
}

type GuestReportReadOnlyProps = {
    intervention: any;
    invitationContext: any;
    invitationUuid: string;
};

/**
 * Vue lecture seule du rapport finalisé pour les invités.
 * Calquée sur ProcedureReportPage.jsx
 */
export default function GuestReportReadOnly({ intervention, invitationContext, invitationUuid }: GuestReportReadOnlyProps) {
    const { t } = useTranslation('public');
    const [playingVideo, setPlayingVideo] = useState<any | null>(null);

    const procedureReport = intervention?.procedureReport;
    const templateVersion = procedureReport?.procedureTemplateVersion;
    const configuration = templateVersion?.configuration || procedureReport?.configuration;
    const answers = procedureReport?.answers;
    const documents = intervention?.documents || [];

    // Map des documents par uniq_id
    const documentsMap: Record<string, any> = {};
    documents.forEach((doc: any) => {
        documentsMap[doc.uniq_id] = doc;
    });

    // Construire les sections avec leurs valeurs
    const sections = (configuration?.sections || []).map((section: any, sectionIndex: number) => {
        const answerSection = answers?.sections?.find((as: any) => as.id === section.id) || {};

        const fields = (section.fields || []).map((field: any) => {
            const answerField = (answerSection as any).fields?.find((af: any) => af.id === field.id);

            // Pour les champs média (photo / vidéo), récupérer les documents liés
            let photos: any[] = [];
            const ft = backendToFrontendFieldType(field.type);
            if ((ft === 'photo' || ft === 'video') && Array.isArray(answerField?.value)) {
                photos = answerField.value.map((docId: string) => {
                    const doc = documentsMap[docId];
                    if (doc) {
                        const isVideo = typeof doc.mime === 'string' && doc.mime.startsWith('video/');
                        // Routes de l'invitation : un invité n'est pas autorisé sur /api/pictures
                        return {
                            ...doc,
                            previewUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'thumbnail'),
                            thumbnailUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'thumbnail'),
                            originalUrl: getGuestPhotoUrl(invitationUuid, doc.uniq_id, 'original'),
                            mediaType: isVideo ? 'video' : 'photo',
                        };
                    }
                    return null;
                }).filter(Boolean);
            }

            return {
                ...field,
                value: answerField?.value ?? '',
                photos,
            };
        });

        return {
            id: section.id || String(sectionIndex + 1),
            name: section.name || section.title || t('report.sectionFallbackName', { number: sectionIndex + 1 }),
            icon: section.icon || '📋',
            fields,
        };
    });

    const templateColor = configuration?.color || '#34DCA7';
    const reportTitle = templateVersion?.name || invitationContext?.procedure_template?.name || t('report.defaultTitle');
    const completedDate = intervention?.updated_at || intervention?.created_at;

    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-card shadow-sm">
                <div className="px-[20px] py-[16px]">
                    <div className="flex items-center gap-[12px] mb-[4px]">
                        <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="h-5 w-5 text-secondary" />
                        </div>
                        <div className="flex-1">
                            <h1 className="text-[18px] text-foreground font-semibold">
                                {reportTitle}
                            </h1>
                            <p className="text-[12px] text-secondary font-medium">{t('report.finalized')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto scrollbar-hide pb-[32px]">
                <div className="px-[20px] pt-[20px]">
                    {/* Info Card */}
                    <div className="bg-card rounded-[16px] p-[20px] mb-[15px] border border-border">
                        <div className="space-y-[10px]">
                            {invitationContext?.company?.name && (
                                <div className="flex justify-between">
                                    <span className="text-[13px] text-muted-foreground">{t('report.companyLabel')}</span>
                                    <span className="text-[13px] font-medium text-foreground">
                                        {invitationContext.company.name}
                                    </span>
                                </div>
                            )}
                            {invitationContext?.folder?.name && (
                                <div className="flex justify-between">
                                    <span className="text-[13px] text-muted-foreground">{t('report.folderLabel')}</span>
                                    <span className="text-[13px] font-medium text-foreground">
                                        {invitationContext.folder.name}
                                    </span>
                                </div>
                            )}
                            {completedDate && (
                                <div className="flex justify-between">
                                    <span className="text-[13px] text-muted-foreground">{t('report.submittedOnLabel')}</span>
                                    <span className="text-[13px] font-medium text-foreground">
                                        <div className="flex items-center gap-[6px]">
                                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                            {formatDate(completedDate)}
                                        </div>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sections */}
                    {sections.map((section: any) => (
                        <div
                            key={section.id}
                            className="bg-card rounded-[16px] p-[20px] mb-[15px] border border-border"
                        >
                            {/* Section Header */}
                            <div className="flex items-center gap-[12px] mb-[15px]">
                                <div
                                    className="w-[32px] h-[32px] rounded-full flex items-center justify-center text-[18px]"
                                    style={{ backgroundColor: `${templateColor}15` }}
                                >
                                    {section.icon}
                                </div>
                                <h3 className="text-[16px] text-foreground font-semibold flex-1">
                                    {section.name}
                                </h3>
                            </div>

                            {/* Fields */}
                            <div className="space-y-[15px]">
                                {section.fields.map((field: any, index: number) => (
                                    <ReadOnlyField
                                        key={field.id || index}
                                        field={field}
                                        value={field.value}
                                        photos={field.photos}
                                        onVideoClick={setPlayingVideo}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Empty state */}
                    {sections.length === 0 && (
                        <div className="bg-card rounded-[16px] p-[40px] border border-border text-center">
                            <p className="text-muted-foreground">{t('report.emptyState')}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Lecture d'une vidéo du rapport */}
            <GuestVideoDialog
                video={playingVideo}
                invitationUuid={invitationUuid}
                onClose={() => setPlayingVideo(null)}
            />
        </div>
    );
}
